import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader, Card, Title, Text, Button, Stack } from "@mantine/core";
import useSignIn from 'react-auth-kit/hooks/useSignIn';
import { notifications } from "@mantine/notifications";
import { UserAuthData } from "../Types/UserDataTypes";

/**
 * OAuth callback handler page.
 *
 * This page receives the OAuth code from Discord after authorization.
 * It calls the /auth/login endpoint which handles both login and auto-registration.
 *
 * IMPORTANT: Discord authorization codes can only be used ONCE.
 * The backend /auth/login endpoint exchanges the code and automatically
 * creates the user if they don't exist.
 *
 * The redirect_uri used for code exchange MUST match the one in the
 * original authorization request (https://recorelab.com/authenticate).
 */
export default function Authenticate() {
    const [searchParams] = useSearchParams();
    const [status, setStatus] = useState<'processing' | 'error' | 'no_code'>('processing');
    const [errorMessage, setErrorMessage] = useState<string>();
    const signIn = useSignIn<UserAuthData>();
    const navigate = useNavigate();

    // Track if we've attempted code exchange to prevent duplicate requests.
    // Discord auth codes are single-use; if the effect re-runs (e.g., due to
    // dependency changes), we must not attempt to exchange the same code again.
    const codeExchangeAttemptedRef = useRef(false);

    useEffect(() => {
        const code = searchParams.get("code");
        const error = searchParams.get("error");
        const errorDescription = searchParams.get("error_description");

        // Handle OAuth error (user denied, etc.)
        if (error) {
            setStatus('error');
            setErrorMessage(errorDescription || error);
            return;
        }

        if (!code) {
            setStatus('no_code');
            return;
        }

        // Guard: only attempt code exchange once per page load
        if (codeExchangeAttemptedRef.current) {
            return;
        }
        codeExchangeAttemptedRef.current = true;

        // The redirect_uri MUST match what was sent in the authorization request
        // In UAT/production, this must be https://recorelab.com/authenticate (configured in Discord)
        // regardless of whether user accessed via web.recorelab.com or recorelab.com
        const apiBase = window.APP_CONFIG?.BASE_API_URL || '';
        const isProduction = apiBase.includes('recorelab.com');
        const redirectUri = isProduction
            ? 'https://recorelab.com/authenticate'
            : `${window.location.origin}/authenticate`;

        // apiBase already determined above - use it for API calls
        // In UAT: api.recorelab.com, in dev: localhost:5229
        const effectiveApiBase = apiBase || window.location.origin;

        async function authenticate() {
            try {
                // Call /auth/login which handles both login and auto-registration
                // IMPORTANT: Discord auth codes can only be used ONCE, so we must not
                // make multiple API calls that try to exchange the same code
                const loginResponse = await fetch(`${effectiveApiBase}/auth/login`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({ authCode: code, redirectUri })
                });

                if (!loginResponse.ok) {
                    const errorText = await loginResponse.text();
                    console.error('Login request failed:', loginResponse.status, errorText);
                    throw new Error('Authentication request failed');
                }

                const loginData = await loginResponse.json();

                if (loginData?.isAuthenticated && loginData?.token) {
                    completeSignIn(loginData);
                    return;
                }

                // Login failed for some reason
                throw new Error(loginData?.statusCode || 'Authentication failed');

            } catch (err) {
                console.error('Authentication error:', err);
                setStatus('error');
                setErrorMessage(err instanceof Error ? err.message : 'Authentication failed');
                notifications.show({
                    title: 'Authentication Failed',
                    message: 'Unable to complete authentication. Please try again.',
                    color: 'red',
                });
            }
        }

        function completeSignIn(auth: { isAuthenticated: boolean; token?: string; refreshToken?: string; user?: any }) {
            try {
                const ok = signIn({
                    auth: { token: auth.token ?? '', type: 'Bearer' },
                    refresh: auth.refreshToken ?? '',
                    userState: {
                        name: auth.user?.name ?? '-',
                        id: auth.user?.id ?? null,
                        roles: auth.user?.roles ?? [],
                    }
                });

                if (ok) {
                    notifications.show({
                        title: 'Welcome!',
                        message: `Signed in as ${auth.user?.name || 'User'}`,
                        color: 'green',
                    });
                    navigate('/');
                    return;
                }
            } catch (e) {
                console.error('Sign-in state error:', e);
            }

            setStatus('error');
            setErrorMessage('Failed to complete sign-in');
        }

        authenticate();
    }, [searchParams, signIn, navigate]);

    if (status === 'processing') {
        return (
            <div className="m-4">
                <div className="flex items-center justify-center min-h-[50vh]">
                    <Card withBorder radius="md" p="xl" style={{ maxWidth: 420, width: '100%' }}>
                        <Stack align="center" gap="md">
                            <Loader size="lg" />
                            <Title order={3}>Authenticating...</Title>
                            <Text size="sm" c="dimmed" ta="center">
                                Please wait while we complete your sign-in with Discord.
                            </Text>
                        </Stack>
                    </Card>
                </div>
            </div>
        );
    }

    if (status === 'no_code') {
        return (
            <div className="m-4">
                <div className="flex items-center justify-center min-h-[50vh]">
                    <Card withBorder radius="md" p="xl" style={{ maxWidth: 420, width: '100%' }}>
                        <Stack align="center" gap="md">
                            <Title order={3}>No Authorization Code</Title>
                            <Text size="sm" c="dimmed" ta="center">
                                This page handles OAuth callbacks. Please use the login or signup page to authenticate.
                            </Text>
                            <Stack gap="sm" w="100%">
                                <Button component="a" href="/login" fullWidth>
                                    Go to Login
                                </Button>
                                <Button component="a" href="/signup" variant="light" fullWidth>
                                    Go to Signup
                                </Button>
                            </Stack>
                        </Stack>
                    </Card>
                </div>
            </div>
        );
    }

    // Error state
    return (
        <div className="m-4">
            <div className="flex items-center justify-center min-h-[50vh]">
                <Card withBorder radius="md" p="xl" style={{ maxWidth: 420, width: '100%' }}>
                    <Stack align="center" gap="md">
                        <Title order={3} c="red">Authentication Failed</Title>
                        <Text size="sm" ta="center">
                            {errorMessage || 'An error occurred during authentication.'}
                        </Text>
                        <Stack gap="sm" w="100%">
                            <Button component="a" href="/login" fullWidth>
                                Try Again
                            </Button>
                            <Button component="a" href="/" variant="light" fullWidth>
                                Go Home
                            </Button>
                        </Stack>
                    </Stack>
                </Card>
            </div>
        </div>
    );
}
